import { Eye, Paperclip } from "lucide-react"
import { useState } from "react"

import type { Orden, TratamientoDiferencia } from "@/componentes/ordenes/tipos"
import { aCentimos, comoMonto, monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { comoFecha } from "@/lib/fechas"

/**
 * El pago de la orden: de qué cuenta salió, cuánto y con qué comprobante.
 *
 * ⚠ **Se enseñan también los pagos revertidos**, tachados y con su motivo. Esconderlos
 * dejaría una orden «aprobada» sin rastro de que ya se intentó pagar una vez, y la
 * pregunta «¿por qué se pagó dos veces?» se contesta mirando justo esa fila.
 *
 * ⚠ **El comprobante se ve como los soportes: con su correlativo y en vista rápida.**
 * Es uno de los documentos de la orden —`OP-2026-0003-02.png`—, así que se nombra
 * igual que en «Respaldo» y se abre en el mismo modal. Era un enlace «Comprobante» que
 * sacaba de la ficha para descargarlo, y no decía cuál de los archivos era.
 *
 * ⚠ **Una orden puede tener VARIOS pagos vivos** (2026-09-27): uno parcial que dejó el
 * resto pendiente y el que lo completa, o saldo disponible de la empresa aplicado. Cada
 * pago dice cuánto cubrió y qué se decidió con su diferencia, y se revierte por
 * separado: revertir «el pago» de una orden con dos no dice cuál.
 */
export function BloquePago({
  orden,
  onRevertir,
  onAnularAplicacion,
}: {
  orden: Orden
  /** Sólo desde Tesorería y con permiso: si falta, no se ofrece. */
  onRevertir?: (ejecucionId: number) => void
  onAnularAplicacion?: (aplicacionId: number) => void
}) {
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  const aplicaciones = orden.aplicaciones_saldo ?? []
  if (orden.ejecuciones.length === 0 && aplicaciones.length === 0) return null
  const pagado = orden.monto_pagado ? aCentimos(orden.monto_pagado) : null
  const pendiente = orden.monto_pendiente ? aCentimos(orden.monto_pendiente) : 0n

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">Pago</h3>
      {pagado !== null && pendiente > 0n && (
        <p className="text-sm">
          <Insignia tono="aviso">Pago parcial</Insignia>{" "}
          <span className="tabular-nums">
            pagado {comoMonto(pagado)} · pendiente {comoMonto(pendiente)} {orden.divisa ?? ""}
          </span>
        </p>
      )}
      <ul className="space-y-2">
        {orden.ejecuciones.map((e) => {
          // En centimos exactos: los tres salen de la cuenta, cada uno en su linea.
          const total =
            aCentimos(e.monto_debitado) + aCentimos(e.comision) + aCentimos(e.monto_igtf)
          return (
            <li
              key={e.id}
              className="tarjeta space-y-1 p-3 text-sm"
              style={e.anulada ? { opacity: 0.6 } : undefined}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Insignia tono={e.anulada ? "neutro" : "exito"}>
                  {e.anulada ? "Revertido" : "Pagado"}
                </Insignia>
                <span className={e.anulada ? "line-through" : undefined}>
                  {e.cuenta ?? "Cuenta"} · {e.metodo_pago ?? "sin método"} · ref. {e.referencia}
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 tabular-nums">
                <dt style={{ color: "var(--fg-muted)" }}>Debitado</dt>
                <dd className="text-right">{monto(e.monto_debitado)}</dd>
                {e.comision !== "0.00" && (
                  <>
                    <dt style={{ color: "var(--fg-muted)" }}>Comisión</dt>
                    <dd className="text-right">{monto(e.comision)}</dd>
                  </>
                )}
                {e.monto_igtf !== "0.00" && (
                  <>
                    <dt style={{ color: "var(--fg-muted)" }}>IGTF</dt>
                    <dd className="text-right">{monto(e.monto_igtf)}</dd>
                  </>
                )}
                <dt style={{ color: "var(--fg-muted)" }}>Salió de la cuenta</dt>
                <dd className="text-right font-semibold">{comoMonto(total)}</dd>
                {e.tasa_aplicada && (
                  <>
                    <dt style={{ color: "var(--fg-muted)" }}>Tasa aplicada</dt>
                    <dd className="text-right">{e.tasa_aplicada}</dd>
                  </>
                )}
                <dt style={{ color: "var(--fg-muted)" }}>Fecha valor</dt>
                <dd className="text-right">{comoFecha(e.fecha_valor)}</dd>
              </dl>
              <Diferencia
                diferencia={e.monto_diferencia}
                tratamiento={e.tratamiento_diferencia}
                moneda={orden.divisa ?? ""}
              />
              {/* O el comprobante, o por qué no lo hay: un pago siempre explica su
                  respaldo, y la base lo garantiza con un `CHECK`. */}
              {e.documento_id !== null ? (
                <Comprobante orden={orden} documentoId={e.documento_id} onVer={setViendo} />
              ) : (
                <p style={{ color: "var(--fg-muted)" }}>
                  Sin comprobante: {e.motivo_sin_comprobante}
                </p>
              )}
              {e.anulada && e.motivo_anulacion && (
                <p style={{ color: "var(--fg-muted)" }}>Revertido: {e.motivo_anulacion}</p>
              )}
              {!e.anulada && onRevertir && (
                <div className="flex justify-end">
                  <Boton tamano="sm" variante="peligro" onClick={() => onRevertir(e.id)}>
                    Revertir este pago
                  </Boton>
                </div>
              )}
            </li>
          )
        })}
        {aplicaciones.map((a) => (
          <li
            key={`a${a.id}`}
            className="tarjeta space-y-1 p-3 text-sm"
            style={a.anulada ? { opacity: 0.6 } : undefined}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Insignia tono={a.anulada ? "neutro" : "exito"}>
                {a.anulada ? "Anulado" : "Saldo de la empresa"}
              </Insignia>
              <span className={a.anulada ? "line-through" : undefined}>
                Saldo disponible de la empresa frente al proveedor · sin movimiento de banco
              </span>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 tabular-nums">
              <dt style={{ color: "var(--fg-muted)" }}>Aplicado</dt>
              <dd className="text-right font-semibold">
                {monto(a.monto)} {orden.divisa ?? ""}
              </dd>
              <dt style={{ color: "var(--fg-muted)" }}>Fecha valor</dt>
              <dd className="text-right">{comoFecha(a.fecha_valor)}</dd>
            </dl>
            {a.anulada && a.motivo_anulacion && (
              <p style={{ color: "var(--fg-muted)" }}>Anulado: {a.motivo_anulacion}</p>
            )}
            {!a.anulada && onAnularAplicacion && (
              <div className="flex justify-end">
                <Boton tamano="sm" variante="peligro" onClick={() => onAnularAplicacion(a.id)}>
                  Anular aplicación
                </Boton>
              </div>
            )}
          </li>
        ))}
      </ul>
      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </section>
  )
}

function Comprobante({
  orden,
  documentoId,
  onVer,
}: {
  orden: Orden
  documentoId: number
  onVer: (archivo: ArchivoParaVer) => void
}) {
  const documento = orden.documentos.find((d) => d.id === documentoId)
  // ⚠ **Sin documento en la ficha es que su ARCHIVO no está**: el servidor no lista los
  // soportes que no puede abrir (incidente del 04/10–09/10/2026). Se dice, y no se ofrece
  // un enlace que daría «Documento no encontrado».
  if (!documento) {
    return (
      <p className="flex items-center gap-2" style={{ color: "var(--warning)" }}>
        <Paperclip size={14} className="shrink-0" aria-hidden={true} />
        Comprobante no disponible: hay que volver a subirlo.
      </p>
    )
  }
  const nombre = documento.nombre || documento.nombre_original
  return (
    <button
      type="button"
      onClick={() =>
        onVer({
          ruta: `/api/v1/ordenes-pago/${orden.id}/documentos/${documentoId}/contenido`,
          nombre,
        })
      }
      title={`Subido como «${documento.nombre_original}»`}
      className="foco-cristal flex min-w-0 items-center gap-2 text-left underline"
    >
      <Paperclip size={14} className="shrink-0" />
      <span className="shrink-0" style={{ color: "var(--fg-muted)" }}>
        Comprobante:
      </span>
      <span className="truncate tabular-nums">{nombre}</span>
      <Eye size={14} className="shrink-0" aria-hidden={true} />
    </button>
  )
}

const DECISION: Record<TratamientoDiferencia, string> = {
  CERRAR: "se cerró la orden con lo pagado",
  SALDO_PROVEEDOR: "el resto quedó como saldo a favor del proveedor, por pagar",
  SALDO_EMPRESA: "el excedente quedó como saldo disponible de la empresa frente al proveedor",
}

/** De menos o de más contra lo pendiente, y qué se decidió. Nada si cuadró. */
function Diferencia({
  diferencia,
  tratamiento,
  moneda,
}: {
  diferencia: string | undefined
  tratamiento: TratamientoDiferencia | null | undefined
  moneda: string
}) {
  if (!diferencia) return null
  const centimos = aCentimos(diferencia)
  if (centimos === 0n) return null
  const deMenos = centimos < 0n
  return (
    <p className="tabular-nums" style={{ color: "var(--warning)" }}>
      Pagado {comoMonto(deMenos ? -centimos : centimos)} {moneda} {deMenos ? "de menos" : "de más"}
      {tratamiento ? `: ${DECISION[tratamiento]}` : ""}.
    </p>
  )
}
